/**
 * Der Simulationstakt. 30 Hz, fest (Kanon §12.4).
 *
 * Diese Datei ist nur noch der **Orchestrator**: Sie führt die Systeme in einer
 * festgeschriebenen Reihenfolge aus. Die Arbeit steckt in `systeme/`.
 *
 * Eine neue Mechanik ist damit eine neue Datei plus eine Zeile in der Liste
 * unten — kein Eingriff in bestehenden Code (Ueberarbeitung A3).
 *
 * VERBOTEN im ganzen Paket: Math.random, Date.now, new Date, performance.now,
 * setTimeout, fetch, window, document. Zufall kommt ausschließlich aus den
 * benannten Strömen. Siehe CLAUDE.md §2.
 */

import type { Zufall } from '@pg/rng';
import type { Absicht, EffektSicht, Ereignis } from '@pg/protocol';
import type { Inhalte } from '@pg/content';
import type { Welt } from './typen.js';
import { system, type BenanntesSystem, type Ctx } from './systeme/typen.js';

import { eingabe } from './systeme/eingabe.js';
import { spielerBewegung, spielerRegeneration, spielerStaerkungen } from './systeme/spieler.js';
import { faehigkeiten, zustandseffekte } from './systeme/kampf.js';
import { gegnerAngriff, gegnerBewegung, merkmale } from './systeme/gegner.js';
import { geschosse } from './systeme/geschosse.js';
import { aufheben, tote } from './systeme/beute.js';
import { abschluss, begleiter } from './systeme/begleiter.js';

export interface SchrittErgebnis {
  ereignisse: Ereignis[];
  effekte: EffektSicht[];
}

/**
 * Die Reihenfolge ist Teil des Spiels, nicht Zufall. Begründungen:
 *
 *  1. `eingabe`      — der Wille des Spielers steht vor allem anderen.
 *  2. `spieler-*`    — er bewegt sich, bevor gerechnet wird, worauf er trifft.
 *  3. `faehigkeiten` — seine Wirkung entsteht aus der neuen Position.
 *  4. `gegner-*`     — Gegner reagieren auf den Stand NACH seiner Aktion.
 *  5. `merkmale`     — laufende Wirkungen, nachdem alle stehen, wo sie stehen.
 *  6. `geschosse`    — fliegen zuletzt, damit sie diesen Takt noch treffen.
 *  7. `zustand`      — Brand und Ähnliches, sekundenweise.
 *  8. `tote`         — erst jetzt abräumen; vorher könnte noch jemand sterben.
 *  9. `begleiter`    — reagiert auf die Beute, die gerade gefallen ist.
 * 10. `aufheben`     — nach dem Begleiter, damit beide dieselbe Lage sehen.
 * 11. `abschluss`    — ganz am Ende. Alles andere darf vorher noch wirken.
 */
const REIHENFOLGE: readonly BenanntesSystem[] = [
  system('eingabe', eingabe),
  system('spieler-bewegung', spielerBewegung),
  system('spieler-regeneration', spielerRegeneration),
  system('spieler-staerkungen', spielerStaerkungen),
  system('faehigkeiten', faehigkeiten),
  system('gegner-bewegung', gegnerBewegung),
  system('gegner-angriff', gegnerAngriff),
  system('merkmale', merkmale),
  system('geschosse', geschosse),
  system('zustandseffekte', zustandseffekte),
  system('tote', tote),
  system('begleiter', begleiter),
  system('aufheben', aufheben),
  system('abschluss', abschluss),
];

/** Namen der Systeme in Ausführungsreihenfolge — für Doku und Tests. */
export function systemNamen(): string[] {
  return REIHENFOLGE.map((s) => s.name);
}

export function schritt(
  welt: Welt,
  zufall: Zufall,
  inhalte: Inhalte,
  absichten: readonly Absicht[],
): SchrittErgebnis {
  const ctx: Ctx = { zufall, inhalte, absichten, ereignisse: [], effekte: [] };

  welt.takt++;

  // Nach dem Abschluss werden nur noch Absichten angenommen (etwa die Wahl
  // einer freigeschalteten Fähigkeit) — die Welt selbst steht still.
  if (welt.stand !== 'laeuft') {
    eingabe(welt, ctx);
    return { ereignisse: ctx.ereignisse, effekte: ctx.effekte };
  }

  for (const s of REIHENFOLGE) s.lauf(welt, ctx);

  return { ereignisse: ctx.ereignisse, effekte: ctx.effekte };
}
